import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  IAdminPatientDetail,
  IAdminUserDetail,
  IUpdatePatientRequest,
  IUpdateUserRequest,
} from "../../../@types/admin/AdminPatient";
import { patientsService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";
import {
  PATIENT_CONDITIONS_QUERY_KEY,
  PATIENT_DETAIL_QUERY_KEY,
  PATIENT_USER_QUERY_KEY,
  PATIENTS_QUERY_KEY,
} from "../../queries/admin/patients";

/**
 * Everything that shows a patient. Called after each mutation, which is the
 * portal's equivalent of Retool's `reloadPatientDetailsTrigger` — a JS query
 * whose whole body was `getPatientDetails.trigger()`, wired into each module as
 * a `reloadPatientDetails` prop.
 */
function invalidatePatient(
  queryClient: ReturnType<typeof useQueryClient>,
  patientId: number
) {
  queryClient.invalidateQueries({
    queryKey: [PATIENT_DETAIL_QUERY_KEY, patientId],
  });
  // The list carries the ward, MRN and password-set columns a save can change.
  queryClient.invalidateQueries({ queryKey: [PATIENTS_QUERY_KEY] });
}

export function useUpdatePatient() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    IAdminPatientDetail,
    Error,
    { patientId: number; request: IUpdatePatientRequest }
  >({
    mutationFn: async ({ patientId, request }) =>
      ensureOk(await patientsService.updatePatient(patientId, request)),
    onSuccess: (_data, { patientId }) =>
      invalidatePatient(queryClient, patientId),
  });

  return { updatePatient: mutateAsync, isUpdatingPatient: isPending };
}

export function useUpdatePatientUser() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    IAdminUserDetail,
    Error,
    { userId: number; patientId: number; request: IUpdateUserRequest }
  >({
    mutationFn: async ({ userId, request }) =>
      ensureOk(await patientsService.updateUser(userId, request)),
    onSuccess: (_data, { userId, patientId }) => {
      queryClient.invalidateQueries({
        queryKey: [PATIENT_USER_QUERY_KEY, userId],
      });
      // The name and email are list columns too.
      invalidatePatient(queryClient, patientId);
    },
  });

  return { updateUser: mutateAsync, isUpdatingUser: isPending };
}

/**
 * Changes the patient's condition.
 *
 * ⚠️ Clinically significant: the condition decides which measurement cards the
 * patient app shows and which workflows evaluate for them. Always called from a
 * confirmation modal.
 */
export function useChangePatientCondition() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    unknown,
    Error,
    { patientId: number; conditionTypeId: number }
  >({
    mutationFn: async ({ patientId, conditionTypeId }) =>
      ensureOk(
        await patientsService.changeCondition(patientId, conditionTypeId)
      ),
    onSuccess: (_data, { patientId }) => {
      queryClient.invalidateQueries({
        queryKey: [PATIENT_CONDITIONS_QUERY_KEY, patientId],
      });
      invalidatePatient(queryClient, patientId);
    },
  });

  return { changeCondition: mutateAsync, isChangingCondition: isPending };
}

/**
 * Resets the patient's password.
 *
 * Takes the USER id, not the patient id — the route is
 * `/Retool/Patients/{userId}/ChangePassword`, which reads as a patient id and
 * is not one. Nothing to invalidate: the response changes no data the page
 * shows.
 */
export function useChangePatientPassword() {
  const { mutateAsync, isPending } = useMutation<
    unknown,
    Error,
    { userId: number; newPassword: string }
  >({
    mutationFn: async ({ userId, newPassword }) =>
      ensureOk(await patientsService.changePassword(userId, newPassword)),
  });

  return { changePassword: mutateAsync, isChangingPassword: isPending };
}

/**
 * Replaces the patient's ward set (EN-5170).
 *
 * ⚠️ Moves the patient's care team, card configuration, workflows and alerts.
 * Invalidates the patient's whole detail view, because almost every panel on it
 * is ward-dependent — this is what Retool's `reloadPatientDetails` prop did
 * from inside the module.
 */
export function useChangePatientWards() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    unknown,
    Error,
    { patientId: number; wardIds: number[] }
  >({
    mutationFn: async ({ patientId, wardIds }) =>
      ensureOk(await patientsService.changeWards(patientId, wardIds)),
    onSuccess: (_data, { patientId }) => {
      invalidatePatient(queryClient, patientId);
      // Every panel keyed on the patient: their workflows, alerts, devices,
      // treatments and protocol actions all resolve through the ward set.
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === "string" && key.startsWith("admin-patient");
        },
      });
    },
  });

  return { changeWards: mutateAsync, isChangingWards: isPending };
}

/** Registers the patient on the eMED integration. */
export function useSendEmedRegistration() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    unknown,
    Error,
    { patientId: number }
  >({
    mutationFn: async ({ patientId }) =>
      ensureOk(await patientsService.sendEmedRegistration(patientId)),
    onSuccess: (_data, { patientId }) =>
      invalidatePatient(queryClient, patientId),
  });

  return { sendEmedRegistration: mutateAsync, isSendingEmed: isPending };
}
